/**
 * Shared motion presets.
 *
 * The rule for this product: motion explains, it does not decorate. A commit
 * that moves lanes slides so you can follow it. A star lands so the reward
 * registers. Nothing eases for longer than it takes to read, and everything
 * here collapses to nothing when the viewer asks for reduced motion.
 */
import type { Transition, Variants } from 'motion/react';

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/** The workhorse: quick, barely overshooting. Used for layout changes. */
export const spring: Transition = { type: 'spring', stiffness: 520, damping: 38, mass: 0.8 };

/** Softer, for things that should feel physical, like a star landing. */
export const springSoft: Transition = { type: 'spring', stiffness: 320, damping: 22, mass: 0.9 };

/** For opacity and colour, where a spring would look wrong. */
export const ease: Transition = { duration: 0.18, ease: [0.2, 0.7, 0.2, 1] };

export const easeSlow: Transition = { duration: 0.32, ease: [0.2, 0.7, 0.2, 1] };

/** Rows entering and leaving a list. */
export const rowVariants: Variants = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0, transition: spring },
  exit: { opacity: 0, y: 6, transition: ease },
};

/** A panel appearing over the page. */
export const panelVariants: Variants = {
  initial: { opacity: 0, y: 14, scale: 0.985 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 380, damping: 32 } },
  exit: { opacity: 0, y: 8, scale: 0.99, transition: ease },
};

/** The scrim behind a panel. */
export const scrimVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: easeSlow },
  exit: { opacity: 0, transition: ease },
};

/** Stagger helper: the nth child of a group, delayed. */
export const staggered = (index: number, step = 0.055, base: Transition = spring): Transition => ({
  ...base,
  delay: index * step,
});
